/**
 * CERES-038 — O CATÁLOGO CONTROLADO DE CAMPOS DINÂMICOS.
 *
 * POR QUE UMA LISTA, E NÃO UM CAMPO LIVRE
 *
 * Um modelo que aceita qualquer `{{coisa}}` publica sem reclamar e imprime
 * `[falta: coisa]` na proposta que vai ao cliente. O erro só aparece do lado de
 * fora, quando não tem mais volta. Com catálogo, a publicação recusa antes: o
 * campo existe ou não existe, e a resposta é do servidor.
 *
 * DUAS CAMADAS, E ELAS NÃO SE MISTURAM
 *
 * 1. **Chave estável** (`empresa`, `total`, `validade`) — é o que vai no corpo do
 *    bloco e o que o compositor substitui. Ela não muda de nome, porque modelo
 *    publicado é imutável e continuaria apontando para a chave antiga.
 * 2. **Rótulo** ("Nome do cliente") — é o que a pessoa vê e escolhe. Ninguém
 *    deveria precisar digitar `{{opportunity.customer.legal_name}}` para pôr o
 *    nome do cliente numa proposta.
 *
 * As chaves aqui são as MESMAS que `variaveisDoCrm` já produzia antes desta
 * missão. Renomeá-las faria os três modelos semeados em produção pararem de
 * resolver — e eles estão vinculados a propostas.
 */

/** Os agrupamentos do seletor. A ordem é a ordem em que a tela os mostra. */
export const PROPOSAL_FIELD_GROUP = [
  'CLIENTE',
  'CONTATO',
  'OPORTUNIDADE',
  'PROPOSTA',
  'COMPOSICAO',
  'FORNECEDOR',
] as const;
export type ProposalFieldGroup = (typeof PROPOSAL_FIELD_GROUP)[number];

export const PROPOSAL_FIELD_GROUP_LABEL: Record<ProposalFieldGroup, string> = {
  CLIENTE: 'Empresa cliente',
  CONTATO: 'Contato',
  OPORTUNIDADE: 'Oportunidade',
  PROPOSTA: 'Proposta',
  COMPOSICAO: 'Composição comercial',
  FORNECEDOR: 'Organização fornecedora',
};

export interface ProposalFieldDefinition {
  /** Chave estável usada em `{{chave}}`. Nunca muda. */
  chave: string;
  grupo: ProposalFieldGroup;
  /** O que a pessoa lê no seletor. */
  rotulo: string;
  /** Valor de demonstração, usado na prévia sem proposta. */
  exemplo: string;
  /**
   * `true` quando o campo é uma TABELA ou bloco inteiro, e não uma palavra no
   * meio de uma frase. A tela não oferece esses dentro de um parágrafo.
   */
  composto?: boolean;
}

export const PROPOSAL_FIELDS: readonly ProposalFieldDefinition[] = [
  // ---- empresa cliente ----------------------------------------------------
  {
    chave: 'empresa',
    grupo: 'CLIENTE',
    rotulo: 'Nome do cliente',
    exemplo: 'Tecelagem Ponto Firme',
  },
  {
    chave: 'razaoSocial',
    grupo: 'CLIENTE',
    rotulo: 'Razão social do cliente',
    exemplo: 'Tecelagem Ponto Firme LTDA',
  },
  { chave: 'cnpj', grupo: 'CLIENTE', rotulo: 'CNPJ do cliente', exemplo: '11.222.333/0001-81' },
  {
    chave: 'enderecoDoCliente',
    grupo: 'CLIENTE',
    rotulo: 'Endereço do cliente',
    exemplo: 'Rua das Indústrias, 400 — Joinville/SC',
  },
  {
    chave: 'siteDoCliente',
    grupo: 'CLIENTE',
    rotulo: 'Site do cliente',
    exemplo: 'www.pontofirme.com.br',
  },

  // ---- contato ------------------------------------------------------------
  { chave: 'contato', grupo: 'CONTATO', rotulo: 'Nome do contato', exemplo: 'Joana Ribeiro' },
  {
    chave: 'cargoDoContato',
    grupo: 'CONTATO',
    rotulo: 'Cargo do contato',
    exemplo: 'Diretora de Operações',
  },
  {
    chave: 'emailDoContato',
    grupo: 'CONTATO',
    rotulo: 'E-mail do contato',
    exemplo: 'joana@pontofirme.com.br',
  },
  {
    chave: 'telefoneDoContato',
    grupo: 'CONTATO',
    rotulo: 'Telefone do contato',
    exemplo: '(47) 3421-0000',
  },

  // ---- oportunidade -------------------------------------------------------
  {
    chave: 'oportunidade',
    grupo: 'OPORTUNIDADE',
    rotulo: 'Título da oportunidade',
    exemplo: 'Modernização da linha de produção',
  },
  {
    chave: 'responsavel',
    grupo: 'OPORTUNIDADE',
    rotulo: 'Responsável comercial',
    exemplo: 'Marcos Antunes',
  },
  {
    chave: 'previsaoDeFechamento',
    grupo: 'OPORTUNIDADE',
    rotulo: 'Previsão de fechamento',
    exemplo: '30/11/2026',
  },
  {
    chave: 'prazoDeExecucao',
    grupo: 'OPORTUNIDADE',
    rotulo: 'Prazo de execução',
    exemplo: '90 dias corridos',
  },

  // ---- proposta -----------------------------------------------------------
  {
    chave: 'numeroDaProposta',
    grupo: 'PROPOSTA',
    rotulo: 'Número da proposta',
    exemplo: 'PROP-2026-00001',
  },
  {
    chave: 'tituloDaProposta',
    grupo: 'PROPOSTA',
    rotulo: 'Título da proposta',
    exemplo: 'Proposta — Modernização da linha',
  },
  { chave: 'versaoDaProposta', grupo: 'PROPOSTA', rotulo: 'Versão da proposta', exemplo: '1' },
  { chave: 'dataDeHoje', grupo: 'PROPOSTA', rotulo: 'Data de emissão', exemplo: '22/09/2026' },
  { chave: 'validade', grupo: 'PROPOSTA', rotulo: 'Validade da proposta', exemplo: '22/10/2026' },

  // ---- composição comercial ----------------------------------------------
  {
    chave: 'tabelaDeInvestimento',
    grupo: 'COMPOSICAO',
    rotulo: 'Tabela de investimento',
    exemplo: '(tabela com os itens negociados)',
    composto: true,
  },
  {
    chave: 'subtotal',
    grupo: 'COMPOSICAO',
    rotulo: 'Subtotal de referência',
    exemplo: 'R$ 250.000,00',
  },
  { chave: 'total', grupo: 'COMPOSICAO', rotulo: 'Total negociado', exemplo: 'R$ 225.000,00' },
  { chave: 'descontoPercentual', grupo: 'COMPOSICAO', rotulo: 'Desconto (%)', exemplo: '10' },
  /*
   * CERES-PRP-011 — O DESCONTO JÁ DITO EM PORTUGUÊS.
   *
   * "+14.65% de desconto" saía de modelos que montavam a frase em volta do
   * número cru. A frase pronta tem vírgula decimal, nenhum sinal e o valor de
   * referência — e fica VAZIA, sem marca de falta, quando não há desconto.
   */
  {
    chave: 'descontoTexto',
    grupo: 'COMPOSICAO',
    rotulo: 'Desconto concedido (frase)',
    exemplo: 'Desconto de 10% sobre o valor de tabela.',
  },
  { chave: 'moeda', grupo: 'COMPOSICAO', rotulo: 'Moeda', exemplo: 'BRL' },
  {
    chave: 'condicoesDePagamento',
    grupo: 'COMPOSICAO',
    rotulo: 'Condição de pagamento',
    exemplo: '30% na assinatura, 70% na entrega',
  },
  {
    chave: 'modeloDeCobranca',
    grupo: 'COMPOSICAO',
    rotulo: 'Modelo de cobrança',
    exemplo: 'Projeto fechado',
  },
  { chave: 'periodicidade', grupo: 'COMPOSICAO', rotulo: 'Periodicidade', exemplo: 'Mensal' },
  /** CERES-PRP-011: cobrança, periodicidade e prazo — só as linhas que existem. */
  {
    chave: 'condicoesComplementares',
    grupo: 'COMPOSICAO',
    rotulo: 'Cobrança, periodicidade e prazo (linhas preenchidas)',
    exemplo: 'Modelo de cobrança: Projeto fechado\nPrazo de execução: 90 dias corridos',
  },

  // ---- organização fornecedora -------------------------------------------
  {
    chave: 'organizacao',
    grupo: 'FORNECEDOR',
    rotulo: 'Nome da sua organização',
    exemplo: 'BugKillers',
  },
  {
    chave: 'razaoSocialDaOrganizacao',
    grupo: 'FORNECEDOR',
    rotulo: 'Razão social da sua organização',
    exemplo: 'BugKillers Tecnologia LTDA',
  },
  {
    chave: 'documentoDaOrganizacao',
    grupo: 'FORNECEDOR',
    rotulo: 'CNPJ da sua organização',
    exemplo: '44.555.666/0001-22',
  },
  {
    chave: 'enderecoDaOrganizacao',
    grupo: 'FORNECEDOR',
    rotulo: 'Endereço da sua organização',
    exemplo: 'Av. Central, 1000 — São Paulo/SP',
  },
  {
    chave: 'telefoneDaOrganizacao',
    grupo: 'FORNECEDOR',
    rotulo: 'Telefone da sua organização',
    exemplo: '(11) 4000-0000',
  },
  {
    chave: 'siteDaOrganizacao',
    grupo: 'FORNECEDOR',
    rotulo: 'Site da sua organização',
    exemplo: 'www.bugkillers.net',
  },
];

const POR_CHAVE = new Map(PROPOSAL_FIELDS.map((campo) => [campo.chave, campo]));

export function campoDeProposta(chave: string): ProposalFieldDefinition | undefined {
  return POR_CHAVE.get(chave);
}

/** As chaves do catálogo, para validação. */
export const PROPOSAL_FIELD_KEYS: readonly string[] = PROPOSAL_FIELDS.map((c) => c.chave);

/** Os valores de demonstração — a prévia sem proposta usa exatamente estes. */
export function valoresDemonstrativos(): Record<string, string> {
  const saida: Record<string, string> = {};
  for (const campo of PROPOSAL_FIELDS) saida[campo.chave] = campo.exemplo;
  return saida;
}

/**
 * CERES-PRP-011 — OS CAMPOS QUE PODEM FICAR VAZIOS SEM SEREM "FALTA".
 *
 * `tabelaDeInvestimento` é desenhada pelo renderizador a partir dos itens, e o
 * texto dela é sempre vazio; `descontoTexto` só existe quando há desconto. Os
 * dois saíam como `[falta: …]` no documento e travavam a finalização por uma
 * ausência que não é ausência de dado nenhum.
 */
export const PROPOSAL_OPTIONAL_FIELD_KEYS: readonly string[] = [
  'tabelaDeInvestimento',
  'descontoTexto',
  'condicoesComplementares',
];
